import React from "react"
import { storyblokEditable } from "@storyblok/react"
import { useStaticQuery, graphql } from "gatsby"
import { BannerNews, BannerNewsTeaser } from "gatsby-theme-gba"

const BannerNewsEditable = ({ blok }) => {
  const defaultLang = process.env.GATSBY_DEFAULT_LANG || "de"

  const blogStories = useStaticQuery(graphql`
    query BannerNewsQuery {
      file: file(relativePath: { eq: "fullwidth-slide.jpg" }) {
        childImageSharp {
          gatsbyImageData
        }
      }
      allFile: allFile(filter: { id: { regex: "/(?:.*)BannerNews/" } }) {
        edges {
          node {
            id
            name
            childImageSharp {
              gatsbyImageData(quality: 80, width: 1360)
            }
          }
        }
      }
      stories: allStoryblokEntry(
        sort: { sort_by_date: DESC }
        filter: {
          default_full_slug: { regex: "/^blog/" }
          is_startpage: { eq: false }
        }
      ) {
        nodes {
          uuid
          tag_list
          lang
          name
          full_slug
          content
          published_at
          sort_by_date
          first_published_at(formatString: "YYYY-MM-DD")
          translated_slugs {
            lang
            path
          }
        }
      }
      fallbackStories: allStoryblokEntry(
        sort: { sort_by_date: DESC }
        filter: { full_slug: { regex: "/^blog/" }, is_startpage: { eq: false } }
      ) {
        nodes {
          uuid
          tag_list
          lang
          name
          full_slug
          content
          published_at
          sort_by_date
          first_published_at(formatString: "YYYY-MM-DD")
          translated_slugs {
            lang
            path
          }
        }
      }
      listingPages: allStoryblokEntry(
        filter: { field_component: { eq: "blogListing" } }
      ) {
        nodes {
          uuid
          lang
          name
          full_slug
          content
          translated_slugs {
            lang
            path
          }
        }
      }
    }
  `)

  const filteredFiles = blogStories.allFile.edges.filter((projectFile) => {
    if (!blok.image || !projectFile.node.id.includes(blok.image.substring(1))) {
      return false
    }
    return true
  })

  const imageFile = filteredFiles[0]
    ? filteredFiles[0].node.childImageSharp.gatsbyImageData
    : "https:" + blok.image

  const todayDate = new Date()
  const today = todayDate.toISOString().split("T")[0]

  const filterLang =
    blok.currentLanguage === defaultLang ? "default" : blok.currentLanguage

  let listingPages = blogStories.listingPages.nodes.filter(
    (node) => node.uuid === blok.newsList
  )

  const currentLanguageListingPage = listingPages.find(
    (node) => node.lang === filterLang
  )

  const customFallbackLang = "en"
  listingPages = listingPages.sort((firstEl, secondEl) => {
    if (firstEl.lang === secondEl.lang) return 0
    if (firstEl.lang === customFallbackLang) return -1
    return 1
  })

  const listingPage = currentLanguageListingPage || listingPages[0]
  if (!listingPage) return null

  const listingPageContent = JSON.parse(listingPage?.content)
  const listingPath = listingPageContent.listingPath

  const allStories = blogStories.stories.nodes.length
    ? blogStories.stories.nodes
    : blogStories.fallbackStories.nodes

  const teasers = allStories.filter((node) => {
    if (listingPath.includes(",")) {
      return listingPath
        .split(",")
        .some(
          (path) =>
            node.full_slug.startsWith(path) && node.first_published_at <= today
        )
    } else {
      return (
        node.full_slug.startsWith(listingPath) &&
        node.first_published_at <= today
      )
    }
  })

  const latestTeasers = teasers.slice(
    0,
    Math.min(blok.maxEntries || 2, teasers.length)
  )

  return (
    <div {...storyblokEditable(blok)} key={blok._uid}>
      <BannerNews
        subHeading={blok.subHeading || "Sub Headline"}
        heading={blok.heading || "Headline"}
        image={imageFile || blogStories.file.childImageSharp.gatsbyImageData}
        imageAlt={blok.imageAlt}
        newsList={blok.newsList}
        newsHeading={blok.newsHeading}
        moreNewsBtnText={blok.moreNewsBtnText}
        moreNewsBtnLink={blok.moreNewsBtnLink.id}
        mode={blok.mode}
        currentLanguage={blok.currentLanguage}
        disableHyphens={blok.disableHyphens}
        maxEntries={blok.maxEntries}
        video={blok.video}
      >
        {latestTeasers.map((teaser, index) => {
          const content = JSON.parse(teaser.content)
          const teasersVisibility = content.body?.[1]?.componentVisibility || []

          if (
            teasersVisibility.length === 0 ||
            teasersVisibility.includes(blok.currentLanguage)
          ) {
            return (
              <BannerNewsTeaser
                key={index}
                heading={content.headline}
                date={teaser.sort_by_date}
                currentLanguage={blok.currentLanguage}
                link={teaser.uuid}
              >
                {content.previewText}
              </BannerNewsTeaser>
            )
          }

          return null
        })}
      </BannerNews>
    </div>
  )
}

export default BannerNewsEditable
